import React, { useState, useMemo, useRef, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { DemoThumbnail, DemoModal } from "@/components/StepDemo";
import {
  Sparkles, ChevronRight, Coins, Crown, User, Building2, ShieldCheck,
  Eye, UserPlus, NotebookPen, Camera, Compass, MapPin, MessageSquare, Zap,
  BookOpen, GraduationCap, Gamepad2, Mic, KeyRound, Trophy, Wand2, Map as MapIcon,
  Megaphone, Heart, Route as RouteIcon, ShoppingBag, Search, LayoutGrid, Blocks,
  Languages, Image as ImageIcon, ListChecks, RefreshCcw, Send, FileText, Terminal,
  ExternalLink, Download, Loader2, Brain,
} from "lucide-react";

/* ─── Role definitions ─────────────────────────────────────────────── */
const ROLES = [
  { key: "visitor",  icon: Eye,          color: "#8B95A1",
    fr: { name: "Visiteur", tag: "Sans compte" },
    en: { name: "Visitor",  tag: "No account" } },
  { key: "free",     icon: UserPlus,     color: "#1F3A5F",
    fr: { name: "Compte gratuit", tag: "0 €" },
    en: { name: "Free account",   tag: "$0" } },
  { key: "amateur",  icon: Crown,        color: "#E5276A",
    fr: { name: "Amateur", tag: "29 €/mois" },
    en: { name: "Amateur", tag: "€29/mo" } },
  { key: "pro",      icon: Building2,    color: "#1F3A5F",
    fr: { name: "Pro (domaine)", tag: "49 €/mois" },
    en: { name: "Pro (winery)",  tag: "€49/mo" } },
  { key: "admin",    icon: ShieldCheck,  color: "#E5276A",
    fr: { name: "Admin", tag: "Back-office" },
    en: { name: "Admin", tag: "Back-office" } },
];

/* ─── Demo player context ─────────────────────────────────────────── */
const DemoCtx = createContext(null);
const useDemo = () => useContext(DemoCtx);

/* ─── Feature card renderer ───────────────────────────────────────── */
function FeatureCard({ icon: Icon, titleFr, titleEn, whyFr, whyEn, stepsFr, stepsEn, where, lang, badgeFr, badgeEn, demo }) {
  const onPlay = useDemo();
  const badge = lang === "fr" ? badgeFr : badgeEn;
  const title = lang === "fr" ? titleFr : titleEn;
  return (
    <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 md:p-6 hover:border-[#E5276A]/40 transition-colors"
         data-testid={`feature-${(titleFr || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}>
      <div className="flex items-start gap-4">
        <span className="p-2.5 rounded-xl bg-[#FCE4ED] text-[#E5276A] shrink-0"><Icon className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg md:text-xl text-[#1F3A5F]">{title}</h3>
            {badge && (
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#1F3A5F]/8 text-[#1F3A5F] uppercase font-bold tracking-wider">
                {badge}
              </span>
            )}
          </div>
          <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">
            <b className="text-[#E5276A]">{lang === "fr" ? "À quoi ça sert : " : "What it's for: "}</b>
            {lang === "fr" ? whyFr : whyEn}
          </p>
          <div className="mt-4 flex flex-col md:flex-row md:items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold mb-1.5">
                {lang === "fr" ? "Comment faire" : "How to do it"}
              </div>
              <ol className="space-y-1.5 text-sm text-[#4A5568] list-decimal list-inside">
                {(lang === "fr" ? stepsFr : stepsEn).map((s, i) => (
                  <li key={i} className="leading-relaxed">{s}</li>
                ))}
              </ol>
              {where && (
                <Link to={where} className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#E5276A] hover:underline">
                  {lang === "fr" ? "Ouvrir" : "Open"} <ChevronRight className="w-3 h-3" />
                </Link>
              )}
            </div>
            {demo && onPlay && (
              <div className="md:shrink-0 md:w-56">
                <DemoThumbnail variant={demo} lang={lang} onClick={() => onPlay({ variant: demo, title })} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Section wrapper ─────────────────────────────────────────────── */
function Section({ title, subtitle, children, testId }) {
  return (
    <section className="mt-10" data-testid={testId}>
      <div className="mb-4">
        <h2 className="font-display text-2xl md:text-3xl text-[#1F3A5F]">{title}</h2>
        {subtitle && <p className="text-sm text-[#8B95A1] mt-1">{subtitle}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/* ─── Content per role ────────────────────────────────────────────── */
function VisitorContent({ lang }) {
  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#FFF8FB] via-white to-[#FBF7F3] border border-[#E5276A]/20 p-5 md:p-7">
        <h2 className="font-display text-2xl text-[#1F3A5F] inline-flex items-center gap-2">
          <Eye className="w-5 h-5 text-[#E5276A]" />
          {lang === "fr" ? "Visiteur — Découvrir sans s'inscrire" : "Visitor — Explore without signing up"}
        </h2>
        <p className="mt-2 text-sm text-[#4A5568]">
          {lang === "fr"
            ? "Vous accédez librement à l'Atlas (appellations, cépages, producteurs) en version blog gratuite (blocs publics), à geoVINUM AI (chat limité), aux jeux en mode invité et à quelques identifications photo par jour. Quotas quotidiens en localStorage, historique non sauvegardé."
            : "You have free access to the Atlas (appellations, grapes, producers) in the free blog version (public blocks), geoVINUM AI (limited chat), games in guest mode, and a few photo IDs per day. Daily quotas stored locally, no history saved."}
        </p>
      </div>

      <Section title={lang === "fr" ? "🏛️ Les 4 piliers conversationnels" : "🏛️ The 4 conversational pillars"} testId="visitor-hubs">
        <FeatureCard lang={lang} icon={Sparkles} where="/hub/experience" demo="chat"
          titleFr="Hub Experience — Billets, événements et scanner"
          titleEn="Experience hub — Tickets, events and scanner"
          whyFr="Point d'entrée pour tout ce qui est événement live : utiliser un code de participant, créer une Experience Pro, ou scanner les billets côté serveur."
          whyEn="Entry point for everything live-event: use a participant code, create a Pro Experience, or scan tickets as a server."
          stepsFr={[
            "Menu latéral → « Experience » (ou /hub/experience).",
            "Choisissez une action (code participant, nouvel événement, scanner).",
            "Posez une question libre à geoVINUM IA : l'app vous aiguille automatiquement vers la bonne page.",
          ]}
          stepsEn={[
            "Sidebar → \"Experience\" (or /hub/experience).",
            "Pick an action (participant code, new event, scanner).",
            "Ask geoVINUM AI any question: the app auto-routes you to the right page.",
          ]}
        />
        <FeatureCard lang={lang} icon={BookOpen} where="/hub/learn" demo="chat"
          titleFr="Hub Learn — Apprendre à son rythme"
          titleEn="Learn hub — Learn at your own pace"
          whyFr="Centralise les Parcours Academy, le Microlearning, les Révisions SRS, l'Atlas (appellations, cépages, producteurs) et votre carnet de notes."
          whyEn="Centralises Academy journeys, Microlearning, SRS reviews, the Atlas (appellations, grapes, producers) and your notebook."
          stepsFr={[
            "Menu → « Learn » (ou /hub/learn).",
            "Cliquez une carte (parcours, micro-leçon, SRS…) ou tapez « Explique-moi Chablis ».",
            "L'IA conversationnelle vous renvoie 2-3 actions clicables + un résumé.",
          ]}
          stepsEn={[
            "Menu → \"Learn\" (or /hub/learn).",
            "Click a card (journey, micro-lesson, SRS…) or type \"Explain Chablis to me\".",
            "The conversational AI replies with 2-3 clickable actions + a summary.",
          ]}
        />
        <FeatureCard lang={lang} icon={Heart} where="/hub/taste" demo="chat"
          titleFr="Hub Taste — Goûter et affiner son palais"
          titleEn="Taste hub — Taste and refine your palate"
          whyFr="Déduisez votre vin, recevez des suggestions personnalisées, consultez votre profil gustatif, identifiez une étiquette par photo."
          whyEn="Deduce your wine, get personalised suggestions, review your taste profile, identify a label by photo."
          stepsFr={[
            "Menu → « Taste » (ou /hub/taste).",
            "Lancez « Déduisez votre vin » (10 questions) ou l'identification photo.",
            "L'IA s'adresse à vous dans la langue de l'app — en portugais si vous êtes en PT, etc.",
          ]}
          stepsEn={[
            "Menu → \"Taste\" (or /hub/taste).",
            "Launch \"Deduce your wine\" (10 questions) or the photo ID flow.",
            "The AI speaks the active app language — Portuguese if you are in PT, etc.",
          ]}
          badgeFr="Nouveau"
          badgeEn="New"
        />
        <FeatureCard lang={lang} icon={Gamepad2} where="/hub/play" demo="game"
          titleFr="Hub Play — Jouer, défier, gagner"
          titleEn="Play hub — Play, challenge, win"
          whyFr="Rassemble Devine l'appellation (le mini-jeu Audrey), les quiz solo/multi, les parties sponsorisées et la boutique récompenses."
          whyEn="Groups Guess the appellation (Audrey mini-game), solo/multi quizzes, sponsored sessions and the rewards shop."
          stepsFr={[
            "Menu → « Play » (ou /hub/play).",
            "Choisissez « Devine l'appellation » pour un défi d'experts, ou rejoignez une partie multi.",
            "Les scores alimentent le leaderboard et le passeport.",
          ]}
          stepsEn={[
            "Menu → \"Play\" (or /hub/play).",
            "Pick \"Guess the appellation\" for an expert challenge, or join a multiplayer game.",
            "Scores feed the leaderboard and the passport.",
          ]}
          badgeFr="Nouveau"
          badgeEn="New"
        />
      </Section>

      <Section title={lang === "fr" ? "🗺️ Explorer l'Atlas" : "🗺️ Explore the Atlas"} testId="visitor-atlas">
        <FeatureCard lang={lang} icon={MapPin} where="/appellations" demo="atlas"
          titleFr="Fiches Appellations"
          titleEn="Appellation pages"
          whyFr="Découvrez toutes les appellations du monde avec les blocs pédagogiques gratuits (histoire, géographie, climat, cépages en aperçu…)."
          whyEn="Discover every appellation of the world with the free pedagogical blocks (history, geography, climate, grapes preview…)."
          stepsFr={[
            "Ouvrez le menu latéral → Discover → Explorer, ou allez directement sur /appellations.",
            "Recherchez une appellation (ex : Chablis, Barolo, Rioja) ou parcourez par région.",
            "Cliquez sur la fiche : vous voyez les blocs marqués « public » (env. 40% du contenu total).",
            "Un bandeau vous invite à créer un compte gratuit pour débloquer les blocs enrichis.",
          ]}
          stepsEn={[
            "Open the sidebar → Discover → Explorer, or go directly to /appellations.",
            "Search for an appellation (e.g. Chablis, Barolo, Rioja) or browse by region.",
            "Click the page: you'll see the blocks tagged as \"public\" (about 40% of the total content).",
            "A banner invites you to create a free account to unlock the enriched blocks.",
          ]}
        />
        <FeatureCard lang={lang} icon={LayoutGrid} where="/cepages" demo="cepage"
          titleFr="Fiches Cépages"
          titleEn="Grape variety pages"
          whyFr="Origine, profil aromatique, climat idéal et régions emblématiques — version gratuite."
          whyEn="Origin, aromatic profile, ideal climate and emblematic regions — free version."
          stepsFr={[
            "Menu latéral → « Explorer » ou allez sur /cepages.",
            "Filtrez par couleur (rouge, blanc, rosé) ou par pays d'origine.",
            "Cliquez sur un cépage → vous voyez les 20 catégories de blocs publics disponibles.",
          ]}
          stepsEn={[
            "Sidebar → \"Explorer\" or go to /cepages.",
            "Filter by colour (red, white, rosé) or country of origin.",
            "Click a grape → you'll see the 20 categories of public blocks available.",
          ]}
        />
        <FeatureCard lang={lang} icon={Building2} where="/producers" demo="producer"
          titleFr="Annuaire Producteurs"
          titleEn="Producer directory"
          whyFr="Fiches domaines avec histoire, cuvées phares et visites œnotouristiques (aperçu gratuit)."
          whyEn="Winery pages with history, flagship cuvées and wine tourism visits (free preview)."
          stepsFr={[
            "Menu → « Producers » ou /producers.",
            "Cherchez par nom, région ou pays.",
            "Certaines fiches revendiquées par un abonné Pro sont enrichies avec Story, Map, événements.",
          ]}
          stepsEn={[
            "Menu → \"Producers\" or /producers.",
            "Search by name, region or country.",
            "Some pages claimed by a Pro subscriber are enriched with Story, Map, events.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "💬 Poser des questions à l'IA" : "💬 Ask the AI"} testId="visitor-ai">
        <FeatureCard lang={lang} icon={MessageSquare} where="/chat" demo="chat"
          titleFr="Chat geoVINUM AI (limité)"
          titleEn="geoVINUM AI Chat (limited)"
          whyFr="Posez n'importe quelle question vin, terroir, accord ou spiritueux."
          whyEn="Ask anything about wine, terroir, pairings or spirits."
          stepsFr={[
            "Cliquez sur « Nouvelle conversation » ou allez sur /chat.",
            "Tapez votre question (ex : « Différence entre Syrah et Shiraz ? »).",
            "Limite : 5 messages/jour en mode visiteur. Créez un compte pour continuer.",
          ]}
          stepsEn={[
            "Click \"New conversation\" or go to /chat.",
            "Type your question (e.g. \"Syrah vs Shiraz?\").",
            "Limit: 5 messages/day in visitor mode. Create an account to continue.",
          ]}
          badgeFr="5/jour"
          badgeEn="5/day"
        />
        <FeatureCard lang={lang} icon={Camera} where="/explorer" demo="photo"
          titleFr="Vision IA — Identifier une bouteille"
          titleEn="AI Vision — Identify a bottle"
          whyFr="Photographiez une étiquette ou un vignoble : geoVINUM AI identifie en 3 secondes."
          whyEn="Photograph a label or vineyard: geoVINUM AI identifies it in 3 seconds."
          stepsFr={[
            "Allez sur /explorer.",
            "Prenez une photo ou uploadez un fichier (étiquette, bouteille, paysage).",
            "L'IA renvoie : appellation supposée, cépages probables, style, notes.",
            "Limite : 3 photos/jour en visiteur.",
          ]}
          stepsEn={[
            "Go to /explorer.",
            "Take a photo or upload a file (label, bottle, landscape).",
            "The AI returns: likely appellation, probable grapes, style, notes.",
            "Limit: 3 photos/day as a visitor.",
          ]}
          badgeFr="3/jour"
          badgeEn="3/day"
        />
      </Section>

      <Section title={lang === "fr" ? "🎮 Jouer" : "🎮 Play"} testId="visitor-play">
        <FeatureCard lang={lang} icon={Brain} where="/audrey" demo="game"
          titleFr="Devine l'appellation (ex-Audrey)"
          titleEn="Guess the appellation (ex-Audrey)"
          whyFr="Un mystère œnologique : geoVINUM IA décrit un terroir mystère, à vous de trouver l'appellation en 5 essais. 100 pts max, bonus si vous trouvez du premier coup."
          whyEn="An œnological mystery: geoVINUM AI describes a mystery terroir, you have 5 tries to guess the appellation. 100 pts max, bonus if you nail it on the first try."
          stepsFr={[
            "Menu → Play → /hub/play → carte « Devine l'appellation » (ou /audrey).",
            "Lisez les 4-6 indices sur climat, sols, cépages, histoire.",
            "Proposez une appellation ; l'IA vous dit si vous êtes proche ou loin, sans révéler.",
            "Demandez un indice supplémentaire si besoin (−10 pts) ou abandonnez.",
            "La réponse et le score s'inscrivent au leaderboard geoVINUM.",
          ]}
          stepsEn={[
            "Menu → Play → /hub/play → card \"Guess the appellation\" (or /audrey).",
            "Read the 4-6 clues on climate, soils, grapes, history.",
            "Submit an appellation; the AI tells you how close you are, without revealing.",
            "Ask for an extra hint if needed (−10 pts) or give up.",
            "Answer and score are logged on the geoVINUM leaderboard.",
          ]}
          badgeFr="Déplacé dans Play"
          badgeEn="Moved to Play"
        />
        <FeatureCard lang={lang} icon={Sparkles} where="/deduce" demo="deduce"
          titleFr="Déduisez votre vin (mode invité)"
          titleEn="Deduce your wine (guest mode)"
          whyFr="Suivez les questions étape par étape (œil, nez, bouche) : geoVINUM AI identifie le profil de votre vin/bière/spiritueux."
          whyEn="Follow the step-by-step questions (eye, nose, palate): geoVINUM AI identifies the profile of your wine/beer/spirit."
          stepsFr={[
            "Allez sur /deduce.",
            "Choisissez le type : vin, bière ou spiritueux.",
            "Répondez aux ~10 questions guidées.",
            "geoVINUM AI livre son verdict + profils compatibles.",
            "Créez un compte pour sauvegarder la déduction dans votre carnet.",
          ]}
          stepsEn={[
            "Go to /deduce.",
            "Pick the type: wine, beer or spirit.",
            "Answer the ~10 guided questions.",
            "geoVINUM AI reveals its verdict + compatible profiles.",
            "Create an account to save the deduction in your notebook.",
          ]}
        />
        <FeatureCard lang={lang} icon={KeyRound} where="/play/join" demo="game"
          titleFr="Rejoindre un quiz avec un code"
          titleEn="Join a quiz with a code"
          whyFr="Un caviste ou un ami vous a partagé un code QR ou un lien : rejoignez la partie live."
          whyEn="A wine shop owner or friend shared a QR code or link with you: join the live game."
          stepsFr={[
            "Allez sur /play/join.",
            "Saisissez le code à 6 caractères OU scannez le QR code.",
            "Choisissez votre pseudo et rejoignez la partie.",
          ]}
          stepsEn={[
            "Go to /play/join.",
            "Enter the 6-character code OR scan the QR code.",
            "Pick a nickname and join the game.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🛒 Boutique et paiement" : "🛒 Shop and checkout"} testId="visitor-shop">
        <FeatureCard lang={lang} icon={ShoppingBag} where="/shop" demo="shop"
          titleFr="Acheter des cartes physiques et goodies"
          titleEn="Buy printed maps and goodies"
          whyFr="La boutique geoVINUM propose cartes viticoles imprimées, livres, cadeaux."
          whyEn="The geoVINUM shop offers printed wine maps, books, and gifts."
          stepsFr={[
            "Menu → « Boutique » ou /shop.",
            "Ajoutez au panier (l'invité peut commander).",
            "Renseignez email et adresse à l'étape checkout.",
            "Paiement sécurisé par Stripe.",
          ]}
          stepsEn={[
            "Menu → \"Shop\" or /shop.",
            "Add to cart (guests can order).",
            "Enter email and shipping address at checkout.",
            "Secure payment via Stripe.",
          ]}
        />
      </Section>
    </>
  );
}

function FreeContent({ lang }) {
  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#FFF8FB] via-white to-[#FBF7F3] border border-[#E5276A]/20 p-5 md:p-7">
        <h2 className="font-display text-2xl text-[#1F3A5F] inline-flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#E5276A]" />
          {lang === "fr" ? "Compte gratuit — Sauvegarder et débloquer les blocs enrichis" : "Free account — Save and unlock enriched blocks"}
        </h2>
        <p className="mt-2 text-sm text-[#4A5568]">
          {lang === "fr"
            ? "Créer un compte débloque immédiatement les blocs enrichis (public: false) sur toutes les fiches Atlas, la sauvegarde du Carnet, le profil aromatique et un crédit offert pour tester les fonctionnalités IA premium. Quotas quotidiens toujours actifs mais plus généreux."
            : "Creating an account instantly unlocks the enriched blocks (public: false) on every Atlas page, Notebook saving, the taste profile, and a free credit to try premium AI features. Daily quotas still apply but are more generous."}
        </p>
      </div>

      <Section title={lang === "fr" ? "📬 Emails & suivi personnalisé" : "📬 Emails & personal follow-up"} testId="free-emails">
        <FeatureCard lang={lang} icon={Send} where="/account" demo="signup"
          titleFr="Séquences email de bienvenue (nurturing)"
          titleEn="Welcome email sequences (nurturing)"
          whyFr="Après chaque inscription ou chaque billet d'Experience obtenu, geoVINUM IA vous envoie une courte séquence email personnalisée (3 à 5 messages) pour vous accompagner dans vos premiers pas et vous suggérer la bonne fonctionnalité au bon moment."
          whyEn="After each sign-up or every Experience ticket, geoVINUM AI sends you a short personalised email sequence (3 to 5 messages) to walk you through your first steps and surface the right feature at the right time."
          stepsFr={[
            "Inscrivez-vous normalement — la séquence démarre automatiquement.",
            "Chaque email contient un CTA direct vers la fonctionnalité suggérée (voyage Academy, carnet, carte…).",
            "Ouvertures et clics sont tracés anonymement et remontent au CRM externe geovinum.io.",
            "Vous pouvez vous désabonner d'un clic depuis n'importe quel email.",
          ]}
          stepsEn={[
            "Sign up as usual — the sequence starts automatically.",
            "Each email includes a direct CTA to the suggested feature (Academy journey, notebook, map…).",
            "Opens and clicks are tracked anonymously and sent to the external CRM at geovinum.io.",
            "You can unsubscribe in one click from any email.",
          ]}
          badgeFr="Nouveau"
          badgeEn="New"
        />
      </Section>

      <Section title={lang === "fr" ? "🚀 Créer et gérer votre compte" : "🚀 Create and manage your account"} testId="free-account">
        <FeatureCard lang={lang} icon={UserPlus} where="/register" demo="signup"
          titleFr="Inscription en 30 secondes"
          titleEn="Sign up in 30 seconds"
          whyFr="Un email + mot de passe suffisent. Aucune carte bancaire requise."
          whyEn="Just an email and password. No credit card required."
          stepsFr={[
            "Allez sur /register.",
            "Renseignez nom, email, mot de passe (min 6 caractères).",
            "Validez : vous êtes connecté automatiquement.",
            "Un crédit test vous est offert pour essayer le Carnet IA ou un Itinéraire.",
          ]}
          stepsEn={[
            "Go to /register.",
            "Enter your name, email, password (min 6 chars).",
            "Confirm: you're logged in automatically.",
            "A test credit is offered to try the AI Notebook or an Itinerary.",
          ]}
        />
        <FeatureCard lang={lang} icon={User} where="/account" demo="signup"
          titleFr="Espace « Mon compte »"
          titleEn={'"My account" page'}
          whyFr="Modifier vos infos, changer de mot de passe, gérer les notifications push, voir votre solde crédits, exporter vos données (RGPD)."
          whyEn="Update your info, change password, manage push notifications, see your credit balance, export your data (GDPR)."
          stepsFr={[
            "Cliquez sur votre avatar (menu latéral bas) ou allez sur /account.",
            "Éditez nom, email, langue préférée.",
            "Activez les notifications push (rappel flashcards du jour).",
            "Consultez votre historique de paiements et crédits restants.",
          ]}
          stepsEn={[
            "Click your avatar (bottom of sidebar) or go to /account.",
            "Edit name, email, preferred language.",
            "Enable push notifications (daily flashcards reminder).",
            "Review your payment history and remaining credits.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "📓 Carnet et Profil gustatif" : "📓 Notebook and Taste Profile"} testId="free-taste">
        <FeatureCard lang={lang} icon={NotebookPen} where="/carnet" demo="notebook"
          titleFr="Carnet de dégustation IA"
          titleEn="AI Tasting Notebook"
          whyFr="Consignez vin, whisky, rhum, bière… en langage libre. geoVINUM AI structure (cuvée, robe, nez, bouche, score) et trouve la photo + le prix."
          whyEn="Log wine, whisky, rum, beer… in free language. geoVINUM AI structures (cuvée, colour, nose, palate, score) and finds photo + price."
          stepsFr={[
            "Menu → « Notes » ou /carnet.",
            "Cliquez sur « Nouvelle dégustation ».",
            "Choisissez le type et tapez vos notes libres.",
            "Cliquez « Enregistrer » : geoVINUM AI structure automatiquement (~4 crédits).",
            "Retrouvez toutes vos dégustations dans la liste, exportables plus tard.",
          ]}
          stepsEn={[
            "Menu → \"Notes\" or /carnet.",
            "Click \"New tasting\".",
            "Pick the type and enter your free-form notes.",
            "Click \"Save\": geoVINUM AI structures it automatically (~4 credits).",
            "Find every tasting in the list, exportable later.",
          ]}
          badgeFr="2/jour gratuits"
          badgeEn="2/day free"
        />
        <FeatureCard lang={lang} icon={User} where="/taste/profile" demo="suggestions"
          titleFr="Profil aromatique personnel"
          titleEn="Personal aromatic profile"
          whyFr="geoVINUM AI analyse votre carnet et dessine votre signature gustative (sucre, corps, tanin, acidité, complexité) + régions/cépages favoris."
          whyEn="geoVINUM AI analyses your notebook and draws your taste signature (sweetness, body, tannin, acidity, complexity) + favourite regions/grapes."
          stepsFr={[
            "Ajoutez au moins 3-5 dégustations dans votre Carnet.",
            "Allez sur /taste/profile.",
            "Cliquez « Créer mon profil » : geoVINUM AI calcule votre radar en ~10 secondes.",
            "Cliquez « Recalculer » pour rafraîchir après de nouvelles dégustations (3 crédits).",
          ]}
          stepsEn={[
            "Add at least 3-5 tastings in your Notebook.",
            "Go to /taste/profile.",
            "Click \"Create my profile\": geoVINUM AI computes your radar in ~10s.",
            "Click \"Recompute\" to refresh after new tastings (3 credits).",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🧭 Explorer plus loin" : "🧭 Explore further"} testId="free-explore">
        <FeatureCard lang={lang} icon={Compass} where="/discover" demo="atlas"
          titleFr="Hub Discover"
          titleEn="Discover Hub"
          whyFr="Point d'entrée unique : appellations, cépages, producteurs, événements, itinéraires, tout à portée."
          whyEn="Single entry point: appellations, grapes, producers, events, itineraries, all in reach."
          stepsFr={["Menu → « Explorer » (Discover)."]}
          stepsEn={["Menu → \"Explorer\" (Discover)."]}
        />
        <FeatureCard lang={lang} icon={MapPin} where="/nearby" demo="nearby"
          titleFr="Around Me — Trouver des caves proches"
          titleEn="Around Me — Find nearby wineries"
          whyFr="Producteurs, cavistes, bars à vin autour de vous via Google Places."
          whyEn="Producers, wine shops, wine bars around you via Google Places."
          stepsFr={[
            "Autorisez la géolocalisation quand le navigateur le demande.",
            "Allez sur /nearby.",
            "Filtrez par type (domaine, caviste, restaurant).",
            "Cliquez sur un point : fiche + itinéraire vers le lieu.",
          ]}
          stepsEn={[
            "Allow geolocation when your browser asks.",
            "Go to /nearby.",
            "Filter by type (winery, wine shop, restaurant).",
            "Click a marker: page + route to the location.",
          ]}
          badgeFr="3/jour"
          badgeEn="3/day"
        />
        <FeatureCard lang={lang} icon={RouteIcon} where="/itineraries" demo="map"
          titleFr="Itinéraires œnotouristiques IA"
          titleEn="AI Wine tourism itineraries"
          whyFr="geoVINUM AI génère un circuit personnalisé selon vos goûts, votre région et votre durée."
          whyEn="geoVINUM AI generates a personalised route based on your taste, region and duration."
          stepsFr={[
            "Allez sur /itineraries.",
            "Choisissez la région et le nombre de jours.",
            "geoVINUM AI propose un itinéraire (~8 crédits).",
            "Sauvegardez, imprimez ou envoyez à un ami.",
          ]}
          stepsEn={[
            "Go to /itineraries.",
            "Pick the region and number of days.",
            "geoVINUM AI proposes an itinerary (~8 credits).",
            "Save, print or send to a friend.",
          ]}
          badgeFr="3/jour"
          badgeEn="3/day"
        />
      </Section>

      <Section title={lang === "fr" ? "🎓 Apprendre (aperçu)" : "🎓 Learn (preview)"} testId="free-learn">
        <FeatureCard lang={lang} icon={Zap} where="/microlearning" demo="flashcard"
          titleFr="Microlearning — Flashcards"
          titleEn="Microlearning — Flashcards"
          whyFr="Capsules de révision espacée (SM-2) : 2-5 min/jour pour retenir cépages, régions et vocabulaire."
          whyEn="Spaced-repetition capsules (SM-2): 2-5 min/day to remember grapes, regions and vocabulary."
          stepsFr={[
            "Menu → Learn → « Microlearning ».",
            "Cliquez « Lancer une session ».",
            "Notez chaque carte : Raté / Difficile / OK / Parfait.",
            "Les cartes ratées reviendront demain automatiquement.",
          ]}
          stepsEn={[
            "Menu → Learn → \"Microlearning\".",
            "Click \"Start a session\".",
            "Rate each card: Missed / Hard / OK / Perfect.",
            "Missed cards come back tomorrow automatically.",
          ]}
          badgeFr="1 deck/jour"
          badgeEn="1 deck/day"
        />
        <FeatureCard lang={lang} icon={BookOpen} where="/courses" demo="course"
          titleFr="Academy — Parcours pédagogiques"
          titleEn="Academy — Learning journeys"
          whyFr="Parcours structurés (WSET-like) : régions, cépages, terroir. Le 1er chapitre est ouvert à tous."
          whyEn="Structured journeys (WSET-like): regions, grapes, terroir. The first chapter is open to everyone."
          stepsFr={[
            "Menu → Learn → « Courses » ou /courses.",
            "Parcourez le catalogue (par niveau, région, langue).",
            "Cliquez sur un cours : lisez librement le premier chapitre.",
            "Un « Coach IA » contextuel apparaît en bas à droite pour répondre à vos questions.",
          ]}
          stepsEn={[
            "Menu → Learn → \"Courses\" or /courses.",
            "Browse the catalogue (by level, region, language).",
            "Click a course: read the first chapter freely.",
            "A contextual \"AI Coach\" appears bottom-right to answer your questions.",
          ]}
        />
      </Section>
    </>
  );
}

function AmateurContent({ lang }) {
  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#FFF8FB] via-white to-[#FBF7F3] border border-[#E5276A]/20 p-5 md:p-7">
        <h2 className="font-display text-2xl text-[#1F3A5F] inline-flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#E5276A]" />
          {lang === "fr" ? "Amateur · 29 €/mois — geoVINUM AI sans limite" : "Amateur · €29/mo — Unlimited geoVINUM AI"}
        </h2>
        <p className="mt-2 text-sm text-[#4A5568]">
          {lang === "fr"
            ? "Tous les quotas quotidiens sautent : chat illimité, photos illimitées, itinéraires illimités, carnet complet sauvegardé à vie, coach personnel, multijoueur, tous les parcours Academy, passport terroir. 100 crédits/mois pour les actions IA lourdes (structuration carnet, itinéraires, blind tasting…)."
            : "All daily quotas removed: unlimited chat, unlimited photos, unlimited itineraries, full notebook saved forever, personal coach, multiplayer, all Academy journeys, terroir passport. 100 credits/month for heavy AI actions (notebook structuring, itineraries, blind tasting…)."}
        </p>
      </div>

      <Section title={lang === "fr" ? "🎯 Prise en main" : "🎯 Getting started"} testId="amateur-onboarding">
        <FeatureCard lang={lang} icon={ListChecks} where="/courses" demo="course"
          titleFr="Checklist de prise en main (3 étapes)"
          titleEn="Onboarding checklist (3 steps)"
          whyFr="Un guide court affiché en haut de la page Courses jusqu'à ce que les 3 étapes clés soient franchies : 3 dégustations, profil aromatique créé, 1er parcours terminé."
          whyEn="A short guide displayed on top of the Courses page until the 3 key steps are done: 3 tastings, taste profile created, 1st journey completed."
          stepsFr={[
            "Allez sur /courses.",
            "La checklist s'affiche en haut avec une barre de progression X/3.",
            "Cliquez sur une étape non-cochée pour aller directement à l'action.",
            "Une fois les 3 étapes complétées, la checklist disparaît automatiquement.",
            "Bouton X pour la masquer manuellement à tout moment.",
          ]}
          stepsEn={[
            "Go to /courses.",
            "The checklist shows on top with an X/3 progress bar.",
            "Click any unchecked step to jump to the action.",
            "Once the 3 steps are done, the checklist auto-hides.",
            "X button dismisses it manually at any time.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🎓 Academy — Formation complète" : "🎓 Academy — Full curriculum"} testId="amateur-academy">
        <FeatureCard lang={lang} icon={Wand2} where="/courses" demo="course"
          titleFr="Narration dynamique + Mastery Quiz final"
          titleEn="Dynamic narration + final Mastery Quiz"
          whyFr="Chaque étape d'un Voyage Academy est désormais narrée en direct par DeepSeek (bulle guide + mini-question d'ancrage). À la fin, un Mastery Quiz est auto-généré sur les 6 dimensions clés (géographie, terroir, cépages, règles AOC, styles, dégustation)."
          whyEn="Every step of an Academy journey is now live-narrated by DeepSeek (guide bubble + anchor question). At the end, a Mastery Quiz is auto-generated across the 6 key dimensions (geography, terroir, grapes, AOC rules, styles, tasting)."
          stepsFr={[
            "Menu → « Courses » et choisissez un parcours.",
            "Progressez étape par étape : les bulles de narration apparaissent une à une.",
            "À la fin du parcours, lancez le Mastery Quiz (8 questions, 4 options).",
            "La note globale ouvre le certificat ; les points faibles sont ajoutés automatiquement à votre Study Schedule.",
          ]}
          stepsEn={[
            "Menu → \"Courses\" and pick a journey.",
            "Progress step by step: narration bubbles appear one after another.",
            "At the end, launch the Mastery Quiz (8 questions, 4 options).",
            "The overall score unlocks the certificate; weak points are auto-added to your Study Schedule.",
          ]}
          badgeFr="Nouveau"
          badgeEn="New"
        />
        <FeatureCard lang={lang} icon={GraduationCap} where="/courses" demo="course"
          titleFr="Parcours illimités"
          titleEn="Unlimited journeys"
          whyFr="Accédez à tout le catalogue Academy en français, anglais, espagnol, portugais."
          whyEn="Access the full Academy catalogue in French, English, Spanish, Portuguese."
          stepsFr={[
            "Menu → « Courses ».",
            "Choisissez un parcours (Explorer / Specialist / Expert).",
            "Progressez chapitre par chapitre : titre, texte, quiz, flashcards, examens.",
            "Le Coach IA en bas à droite répond à vos questions avec le contexte du chapitre courant.",
            "À la fin, vous recevez un certificat vérifiable + XP + badges.",
          ]}
          stepsEn={[
            "Menu → \"Courses\".",
            "Pick a journey (Explorer / Specialist / Expert).",
            "Progress chapter by chapter: title, text, quiz, flashcards, exams.",
            "The AI Coach bottom-right answers your questions with the current chapter's context.",
            "At the end you get a verifiable certificate + XP + badges.",
          ]}
        />
        <FeatureCard lang={lang} icon={Trophy} where="/passport" demo="course"
          titleFr="Passeport Terroir"
          titleEn="Terroir Passport"
          whyFr="Chaque parcours terminé tamponne votre passeport. Un badge par région/thème."
          whyEn="Every completed journey stamps your passport. One badge per region/theme."
          stepsFr={[
            "Terminez au moins un parcours Academy.",
            "Allez sur /passport pour voir votre carte du monde des tampons.",
            "Partagez votre passeport public (lien social).",
          ]}
          stepsEn={[
            "Complete at least one Academy journey.",
            "Go to /passport to see your world map of stamps.",
            "Share your public passport (social link).",
          ]}
        />
        <FeatureCard lang={lang} icon={FileText} where="/revisions" demo="flashcard"
          titleFr="Study Schedule (révisions espacées SRS)"
          titleEn="Study Schedule (spaced-repetition SRS)"
          whyFr="Toutes vos erreurs de quiz et flashcards remontent automatiquement dans /revisions. Un badge doré 🔥 « X day streak » s'affiche après chaque jour consécutif de révision."
          whyEn={'All your quiz and flashcard mistakes surface automatically in /revisions. A golden 🔥 "X day streak" badge appears after each consecutive day of review.'}
          stepsFr={[
            "Menu → « Study Schedule ».",
            "Une pastille rouge indique les cartes en retard.",
            "Lancez une session : 5-10 min suffisent pour revoir les plus urgentes.",
            "Notez chaque carte de 0 à 5 : les mauvaises reviennent demain, les bonnes s'espacent (1j, 3j, 7j, 14j, 30j…).",
            "Chaque jour consécutif fait grimper votre streak. Un jour manqué remet le compteur à 1.",
          ]}
          stepsEn={[
            "Menu → \"Study Schedule\".",
            "A red badge shows overdue cards.",
            "Start a session: 5-10 min is enough to review the most urgent ones.",
            "Rate each card from 0-5: misses come back tomorrow, hits get spaced (1d, 3d, 7d, 14d, 30d…).",
            "Every consecutive day grows your streak. A missed day resets it to 1.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🎯 Coach personnel IA" : "🎯 Personal AI Coach"} testId="amateur-coach">
        <FeatureCard lang={lang} icon={Sparkles} where="/learn/coach" demo="coach"
          titleFr="Coach — Parcours sur mesure"
          titleEn="Coach — Tailored curriculum"
          whyFr="geoVINUM AI vous évalue, détecte vos lacunes et vous construit un parcours personnalisé."
          whyEn="geoVINUM AI evaluates you, spots your gaps and builds you a personalised journey."
          stepsFr={[
            "Menu → Learn → « Coach personnel ».",
            "Passez l'évaluation initiale (~15 questions).",
            "geoVINUM AI liste vos forces et 3 axes prioritaires.",
            "Suivez le plan hebdomadaire (parcours, quiz, flashcards).",
          ]}
          stepsEn={[
            "Menu → Learn → \"Personal coach\".",
            "Take the initial assessment (~15 questions).",
            "geoVINUM AI lists your strengths and 3 priority areas.",
            "Follow the weekly plan (journeys, quizzes, flashcards).",
          ]}
        />
        <FeatureCard lang={lang} icon={BookOpen} where="/learn/explain" demo="photo"
          titleFr="Explain par photo"
          titleEn="Explain by photo"
          whyFr="Photo d'une carte des vins ou d'un terroir → geoVINUM AI vulgarise au niveau choisi (Explorer/Specialist/Expert)."
          whyEn="Photo of a wine list or terroir map → geoVINUM AI explains it at your chosen level (Explorer/Specialist/Expert)."
          stepsFr={[
            "Allez sur /learn/explain.",
            "Uploadez ou photographiez la carte.",
            "Choisissez le niveau de langage souhaité.",
            "geoVINUM AI décode chaque appellation + suggère un vin pour vous.",
          ]}
          stepsEn={[
            "Go to /learn/explain.",
            "Upload or photograph the list.",
            "Pick your language level.",
            "geoVINUM AI decodes each appellation + suggests a wine for you.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🎮 Multijoueur et compétitions" : "🎮 Multiplayer and competitions"} testId="amateur-play">
        <FeatureCard lang={lang} icon={Gamepad2} where="/multiplayer" demo="game"
          titleFr="Multiplayer (1v1, blind, speed)"
          titleEn="Multiplayer (1v1, blind, speed)"
          whyFr="Affrontez d'autres œnophiles en temps réel (WebSocket)."
          whyEn="Compete against other wine lovers live (WebSocket)."
          stepsFr={[
            "Menu → « Lancer un jeu » ou /play.",
            "Choisissez le mode (1v1, Blind Tasting, Speed Race).",
            "Créez une room et partagez le code, ou rejoignez une room ouverte.",
            "Classement live + points XP à la clé.",
          ]}
          stepsEn={[
            "Menu → \"Launch a game\" or /play.",
            "Choose the mode (1v1, Blind Tasting, Speed Race).",
            "Create a room and share the code, or join an open room.",
            "Live leaderboard + XP points at stake.",
          ]}
        />
        <FeatureCard lang={lang} icon={Trophy} where="/play/rewards" demo="game"
          titleFr="Récompenses et badges"
          titleEn="Rewards and badges"
          whyFr="Tous les niveaux et badges débloqués : profil public, récompenses partenaires."
          whyEn="All levels and badges unlocked: public profile, partner rewards."
          stepsFr={["Menu → Play → « Récompenses »."]}
          stepsEn={["Menu → Play → \"Rewards\"."]}
        />
      </Section>

      <Section title={lang === "fr" ? "💡 Suggestions personnalisées" : "💡 Personalised suggestions"} testId="amateur-suggest">
        <FeatureCard lang={lang} icon={Heart} where="/taste/suggestions" demo="suggestions"
          titleFr="Suggestions hebdomadaires"
          titleEn="Weekly suggestions"
          whyFr="geoVINUM AI vous propose chaque semaine 3 cuvées similaires, 3 pour élargir l'horizon, 1 coup de cœur."
          whyEn="Each week, geoVINUM AI suggests 3 similar wines, 3 to broaden your horizon, 1 favourite."
          stepsFr={[
            "Allez sur /taste/suggestions.",
            "Consultez les 7 suggestions.",
            "Cliquez sur une cuvée pour voir la fiche, le score de compatibilité et où l'acheter.",
          ]}
          stepsEn={[
            "Go to /taste/suggestions.",
            "See the 7 suggestions.",
            "Click a cuvée to see the page, compatibility score and where to buy.",
          ]}
        />
      </Section>
    </>
  );
}

function ProContent({ lang }) {
  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#FFF8FB] via-white to-[#FBF7F3] border border-[#E5276A]/20 p-5 md:p-7">
        <h2 className="font-display text-2xl text-[#1F3A5F] inline-flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#E5276A]" />
          {lang === "fr" ? "Pro · 49 €/mois — Domaines viticoles" : "Pro · €49/mo — Wineries"}
        </h2>
        <p className="mt-2 text-sm text-[#4A5568]">
          {lang === "fr"
            ? "Tout ce qui est Amateur PLUS un tableau de bord Pro : AI Onboarding pour générer votre fiche domaine, Story Builder (7 sections éditoriales IA), Game Builder (quiz sponsorisés + capture de leads), Map Builder (parcours de visite, cartes parcellaires, affiches 360° imprimables). 300 crédits/mois."
            : "Everything in Amateur PLUS a Pro dashboard: AI Onboarding to generate your winery page, Story Builder (7 AI editorial sections), Game Builder (sponsored quizzes + lead capture), Map Builder (visit routes, parcel maps, printable 360° posters). 300 credits/month."}
        </p>
      </div>

      <Section title={lang === "fr" ? "🏠 Hub Pro" : "🏠 Pro Hub"} testId="pro-hub">
        <FeatureCard lang={lang} icon={LayoutGrid} where="/pro" demo="story"
          titleFr="Page d'accueil Pro (mode hub)"
          titleEn="Pro home page (hub mode)"
          whyFr="La page /pro s'adapte à votre statut : si vous êtes Pro/admin, elle devient un hub raccourcis avec 4 grandes tuiles cliquables vers les builders + la checklist onboarding en haut. Si vous n'êtes pas encore Pro, elle affiche la page commerciale classique."
          whyEn="The /pro page adapts to your status: if you're Pro/admin, it becomes a shortcut hub with 4 big clickable tiles to the builders + the onboarding checklist on top. If not yet Pro, it shows the standard sales landing."
          stepsFr={[
            "Menu → « Présentation » (Espace Pro).",
            "En haut : checklist des 4 étapes clés (voir plus bas).",
            "Tuiles Dashboard, Fiche domaine, Map Builder, Quiz sponsorisés — accès direct.",
          ]}
          stepsEn={[
            "Menu → \"Overview\" (Pro space).",
            "Top: 4-step onboarding checklist (see below).",
            "Tiles for Dashboard, Winery page, Map Builder, Sponsored quizzes — direct access.",
          ]}
        />
        <FeatureCard lang={lang} icon={ListChecks} where="/pro/dashboard" demo="bulk-toggle"
          titleFr="Onboarding Pro (checklist 4 étapes)"
          titleEn="Pro Onboarding (4-step checklist)"
          whyFr="4 étapes détectées automatiquement pour vous mettre le pied à l'étrier : (1) Créer votre fiche domaine, (2) Publier votre Story, (3) Créer une carte, (4) Lancer un quiz sponsorisé. Une progress bar en direct + bouton X pour masquer."
          whyEn="4 auto-detected steps to get you started: (1) Create your winery page, (2) Publish your Story, (3) Build a map, (4) Launch a sponsored quiz. Live progress bar + X to dismiss."
          stepsFr={[
            "Affiché sur /pro et /pro/dashboard tant que non complet.",
            "Chaque step est cliquable et vous emmène directement à la page correspondante.",
            "Steps validés : icône verte, texte barré.",
            "Auto-disparition quand 4/4 (ou dismiss manuel persisté).",
          ]}
          stepsEn={[
            "Displayed on /pro and /pro/dashboard while incomplete.",
            "Every step is clickable and takes you straight to the matching page.",
            "Completed steps: green check, strikethrough.",
            "Auto-hides at 4/4 (or manual dismiss is persisted).",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🏢 Onboarding et fiche domaine" : "🏢 Onboarding and winery page"} testId="pro-onboard">
        <FeatureCard lang={lang} icon={Building2} where="/pro/dashboard" demo="signup"
          titleFr="AI Onboarding conversationnel"
          titleEn="Conversational AI Onboarding"
          whyFr="10 minutes de conversation avec geoVINUM AI et votre fiche domaine complète est générée automatiquement."
          whyEn="10 minutes of conversation with geoVINUM AI and your full winery page is generated automatically."
          stepsFr={[
            "Souscrivez au plan Pro depuis /pricing.",
            "Allez sur /pro/dashboard.",
            "Cliquez « Créer ma fiche domaine ».",
            "geoVINUM AI vous pose 12-15 questions (histoire, cépages, philosophie, visites).",
            "Une fiche complète est publiée sur /producers/{votre-slug}.",
          ]}
          stepsEn={[
            "Subscribe to the Pro plan from /pricing.",
            "Go to /pro/dashboard.",
            "Click \"Create my winery page\".",
            "geoVINUM AI asks you 12-15 questions (history, grapes, philosophy, visits).",
            "A full page is published at /producers/{your-slug}.",
          ]}
        />
        <FeatureCard lang={lang} icon={Wand2} where="/pro/dashboard" demo="story"
          titleFr="Story Builder — 7 sections éditoriales"
          titleEn="Story Builder — 7 editorial sections"
          whyFr="Histoire, terroir, vins, philosophie, visites, anecdotes, chiffres-clés : chaque section est régénérable à volonté."
          whyEn="History, terroir, wines, philosophy, visits, anecdotes, key figures: each section is re-generatable at will."
          stepsFr={[
            "Depuis /pro/dashboard → onglet « Story ».",
            "Cliquez sur une section pour la voir en preview.",
            "« Régénérer » relance geoVINUM AI avec de nouvelles orientations.",
            "« Éditer » permet la retouche manuelle.",
            "Sauvegardez et publiez.",
          ]}
          stepsEn={[
            "From /pro/dashboard → \"Story\" tab.",
            "Click a section to preview it.",
            "\"Regenerate\" prompts geoVINUM AI with fresh angles.",
            "\"Edit\" allows manual polish.",
            "Save and publish.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🗺️ Cartes et parcours de visite" : "🗺️ Maps and visit routes"} testId="pro-maps">
        <FeatureCard lang={lang} icon={MapIcon} where="/pro/map-builder" demo="map-builder"
          titleFr="Map Builder — 3 artefacts IA"
          titleEn="Map Builder — 3 AI artefacts"
          whyFr="Créez : un parcours de visite guidé, une carte parcellaire de votre domaine, une affiche 360° imprimable HD."
          whyEn="Create: a guided visit route, a parcel map of your winery, a printable HD 360° poster."
          stepsFr={[
            "Menu → « Espace Pro » → /pro/map-builder.",
            "Choisissez le type d'artefact (parcours, parcellaire, affiche 360°).",
            "Renseignez les points clés (parcelles GPS, arrêts, textes courts).",
            "geoVINUM AI génère la carte (10 crédits/artefact).",
            "Téléchargez en PNG/PDF ou intégrez via iframe sur votre site.",
          ]}
          stepsEn={[
            "Menu → \"Pro space\" → /pro/map-builder.",
            "Pick the artefact type (route, parcels, 360° poster).",
            "Enter key points (GPS parcels, stops, short texts).",
            "geoVINUM AI generates the map (10 credits/artefact).",
            "Download as PNG/PDF or embed via iframe on your site.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🎯 Jeux sponsorisés et capture de leads" : "🎯 Sponsored games and lead capture"} testId="pro-games">
        <FeatureCard lang={lang} icon={Megaphone} where="/pro/sponsored" demo="sponsored"
          titleFr="Game Builder — Quiz, chasse au trésor, memory"
          titleEn="Game Builder — Quiz, treasure hunt, memory"
          whyFr="Créez des jeux sponsorisés à jouer sur salon, boutique ou en cave. Récompenses, formulaires de contact, lead qualifiée."
          whyEn="Create sponsored games to play at fairs, shops or in-cellar. Rewards, contact forms, qualified leads."
          stepsFr={[
            "Menu → « Quiz sponsorisés » ou /pro/sponsored.",
            "Cliquez « Nouveau jeu ».",
            "Choisissez le format (Quiz, Chasse au trésor, Memory).",
            "geoVINUM AI génère 10-20 questions à partir de votre profil domaine (20 crédits).",
            "Configurez la récompense (dégustation offerte, remise, cadeau).",
            "Récupérez le QR code et le lien de partage.",
            "Chaque joueur ajoute son email pour recevoir sa récompense = lead en base.",
          ]}
          stepsEn={[
            "Menu → \"Sponsored quizzes\" or /pro/sponsored.",
            "Click \"New game\".",
            "Pick the format (Quiz, Treasure hunt, Memory).",
            "geoVINUM AI generates 10-20 questions from your winery profile (20 credits).",
            "Set the reward (free tasting, discount, gift).",
            "Grab the QR code and share link.",
            "Every player adds their email to get their reward = lead in your CRM.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "📊 Tableau de bord et intégrations" : "📊 Dashboard and integrations"} testId="pro-dash">
        <FeatureCard lang={lang} icon={LayoutGrid} where="/pro/dashboard" demo="suggestions"
          titleFr="Statistiques de fréquentation et leads"
          titleEn="Traffic and lead stats"
          whyFr="Vues fiche, jeux joués, leads collectés, avis reçus, événements upcoming."
          whyEn="Page views, games played, leads collected, reviews received, upcoming events."
          stepsFr={[
            "Allez sur /pro/dashboard onglet « Stats ».",
            "Filtrez par période (7j, 30j, 90j).",
            "Exportez les leads en CSV.",
          ]}
          stepsEn={[
            "Go to /pro/dashboard \"Stats\" tab.",
            "Filter by period (7d, 30d, 90d).",
            "Export leads as CSV.",
          ]}
        />
        <FeatureCard lang={lang} icon={ExternalLink} where="/pro" demo="map-builder"
          titleFr="Intégrations iframe et API"
          titleEn="iframe and API integrations"
          whyFr="Intégrez la carte interactive de votre appellation ou domaine sur votre site en une ligne de HTML."
          whyEn="Embed your appellation or winery interactive map on your site in one line of HTML."
          stepsFr={[
            "Menu → « Espace Pro » → /pro.",
            "Copiez le code iframe pour la carte de votre appellation.",
            "Personnalisez couleurs, langue, layers dans l'URL.",
            "Collez sur votre site : c'est en ligne en 5 minutes.",
          ]}
          stepsEn={[
            "Menu → \"Pro space\" → /pro.",
            "Copy the iframe code for your appellation map.",
            "Customize colors, language, layers via URL params.",
            "Paste on your site: live in 5 minutes.",
          ]}
        />
      </Section>
    </>
  );
}

function AdminContent({ lang }) {
  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-[#FFF8FB] via-white to-[#FBF7F3] border border-[#E5276A]/20 p-5 md:p-7">
        <h2 className="font-display text-2xl text-[#1F3A5F] inline-flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#E5276A]" />
          {lang === "fr" ? "Admin — Back-office externe" : "Admin — External back-office"}
        </h2>
        <p className="mt-2 text-sm text-[#4A5568]">
          {lang === "fr"
            ? "Le panneau admin vit sur "
            : "The admin panel lives at "}
          <a href="https://geovinum.io/admin" target="_blank" rel="noopener noreferrer"
             className="text-[#E5276A] font-bold underline">geovinum.io/admin</a>
          {lang === "fr"
            ? ". Il consomme les APIs FastAPI de l'application via des endpoints authentifiés (rôle admin requis)."
            : ". It consumes the app's FastAPI endpoints via admin-authenticated routes."}
        </p>
        <p className="mt-2 text-sm text-[#4A5568]">
          {lang === "fr"
            ? "Connexion : email admin + mot de passe. Les comptes admin ont un contournement automatique des quotas et de l'abonnement dans toute l'app."
            : "Login: admin email + password. Admin accounts automatically bypass quotas and subscription checks across the entire app."}
        </p>
      </div>

      <Section title={lang === "fr" ? "📚 Atlas Blocks Composer (Phase 7)" : "📚 Atlas Blocks Composer (Phase 7)"} testId="admin-atlas-blocks">
        <FeatureCard lang={lang} icon={Blocks} demo="generate-blocks"
          titleFr="Générer des blocs pédagogiques par IA"
          titleEn="Generate pedagogical blocks with AI"
          whyFr="Décomposez une appellation, un cépage ou un producteur en 20 catégories (Histoire, Climat, Terroir, Cépages, Vinification, Accords, Anecdotes, Chiffres-clés, Œnotourisme, Glossaire, Le Saviez-vous, Mythes, Science…). Chaque catégorie produit 15-20 blocs autonomes."
          whyEn="Break down an appellation, grape or producer into 20 categories (History, Climate, Terroir, Grapes, Vinification, Pairings, Anecdotes, Key figures, Wine tourism, Glossary, Did you know, Myths, Science…). Each category yields 15-20 self-contained blocks."
          stepsFr={[
            "Sur geovinum.io/admin, allez dans « Atlas Blocks ».",
            "Cherchez l'entité (ex : appellation « aloxe-corton »).",
            "Cliquez « Générer les blocs » et cochez les catégories voulues (ou toutes).",
            "L'endpoint POST /api/admin/atlas-blocks/generate renvoie un job_id (génération asynchrone, 60-120s/catégorie).",
            "Le back-office affiche une barre de progression en pollant /api/admin/atlas-blocks/jobs/{job_id} toutes les 3s.",
            "À la fin, les blocs sont visibles dans l'onglet correspondant à chaque catégorie.",
          ]}
          stepsEn={[
            "On geovinum.io/admin, go to \"Atlas Blocks\".",
            "Search the entity (e.g. appellation \"aloxe-corton\").",
            "Click \"Generate blocks\" and tick the desired categories (or all).",
            "The POST /api/admin/atlas-blocks/generate endpoint returns a job_id (async, 60-120s per category).",
            "The back-office shows a progress bar by polling /api/admin/atlas-blocks/jobs/{job_id} every 3s.",
            "When done, blocks appear in each category tab.",
          ]}
        />
        <FeatureCard lang={lang} icon={Eye} demo="bulk-toggle"
          titleFr="Publier / dépublier en masse"
          titleEn="Bulk publish/unpublish"
          whyFr="Chaque bloc porte un flag `public: true|false`. Public = affiché sur blog gratuit + visiteurs. Non public = réservé aux abonnés/connectés."
          whyEn="Each block has a `public: true|false` flag. Public = shown on free blog + visitors. Non-public = reserved for subscribers/logged-in users."
          stepsFr={[
            "Sélectionnez plusieurs blocs (cases à cocher).",
            "Cliquez « Rendre public » ou « Rendre premium ».",
            "L'endpoint PATCH /api/admin/atlas-blocks/bulk applique en une seule requête.",
            "Le cache TTL de 5 min est invalidé automatiquement.",
          ]}
          stepsEn={[
            "Select multiple blocks (checkboxes).",
            "Click \"Make public\" or \"Make premium\".",
            "PATCH /api/admin/atlas-blocks/bulk applies in a single request.",
            "The 5-min TTL cache is invalidated automatically.",
          ]}
        />
        <FeatureCard lang={lang} icon={ImageIcon} demo="photo"
          titleFr="Média — Upload et resize auto"
          titleEn="Media — Upload with auto-resize"
          whyFr="Ajoutez une image à un bloc : elle est auto-redimensionnée à 1600px, convertie en JPEG progressif, EXIF nettoyé."
          whyEn="Attach an image to a block: it's auto-resized to 1600px, converted to progressive JPEG, EXIF stripped."
          stepsFr={[
            "Ouvrez un bloc en édition.",
            "Onglet « Média » → « Uploader une image ».",
            "POST /api/admin/atlas-blocks/{block_id}/media (multipart, max 15 Mo).",
            "L'image apparaît dans le bloc côté frontend, avec caption optionnelle.",
          ]}
          stepsEn={[
            "Open a block in edit mode.",
            "\"Media\" tab → \"Upload image\".",
            "POST /api/admin/atlas-blocks/{block_id}/media (multipart, max 15 MB).",
            "Image appears in the block on the frontend, with optional caption.",
          ]}
        />
        <FeatureCard lang={lang} icon={Search} demo="bulk-toggle"
          titleFr="Détection de doublons"
          titleEn="Duplicate detection"
          whyFr="Un algorithme Levenshtein regroupe les blocs aux titres quasi identiques pour éviter la redondance."
          whyEn="A Levenshtein algorithm clusters blocks with near-identical titles to avoid redundancy."
          stepsFr={[
            "Onglet « Doublons ».",
            "GET /api/admin/atlas-blocks/duplicates?entity_type=…&slug=…&threshold=3.",
            "Chaque cluster liste les blocs à fusionner ou supprimer.",
          ]}
          stepsEn={[
            "\"Duplicates\" tab.",
            "GET /api/admin/atlas-blocks/duplicates?entity_type=…&slug=…&threshold=3.",
            "Each cluster lists blocks to merge or delete.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🎓 Academy Composer" : "🎓 Academy Composer"} testId="admin-academy">
        <FeatureCard lang={lang} icon={GraduationCap} demo="course"
          titleFr="Composer un parcours à partir de blocs Atlas"
          titleEn="Compose a journey from Atlas blocks"
          whyFr="Sélectionnez N blocs de l'Atlas (dans l'ordre) et geoVINUM AI génère les transitions narratives entre chaque section pour former un cours cohérent."
          whyEn="Select N Atlas blocks (in order) and geoVINUM AI generates the narrative transitions between each section to form a coherent course."
          stepsFr={[
            "Sur le back-office, section « Academy Composer ».",
            "Cochez les blocs à inclure, faites glisser pour ordonner.",
            "Remplissez titre, sous-titre, niveau, région, XP.",
            "POST /api/admin/academy/compose (avec generate_transitions=true).",
            "Un draft est créé. Éditez, publiez, ou déclenchez la traduction auto (EN/ES/PT).",
          ]}
          stepsEn={[
            "In the back-office, \"Academy Composer\" section.",
            "Check the blocks to include, drag to reorder.",
            "Fill title, subtitle, level, region, XP.",
            "POST /api/admin/academy/compose (with generate_transitions=true).",
            "A draft is created. Edit, publish, or trigger auto-translation (EN/ES/PT).",
          ]}
        />
        <FeatureCard lang={lang} icon={ListChecks} demo="bulk-toggle"
          titleFr="Drag & Drop et régénération par bloc"
          titleEn="Drag & Drop and per-block regeneration"
          whyFr="Réorganisez les blocs d'un cours ou lancez une action IA sur un bloc spécifique (regenerate, develop, simplify, translate, create_quiz…)."
          whyEn="Reorder blocks of a course or run an AI action on a specific block (regenerate, develop, simplify, translate, create_quiz…)."
          stepsFr={[
            "Ouvrez un cours en édition.",
            "Glissez-déposez les blocs : PATCH /api/admin/academy/generated/{course_id}/blocks/reorder.",
            "Bouton « Régénérer » sur un bloc → POST .../blocks/{block_id}/regenerate avec action au choix.",
            "Le bloc est remplacé (regenerate) ou de nouveaux blocs sont insérés (create_quiz).",
          ]}
          stepsEn={[
            "Open a course in edit mode.",
            "Drag-drop blocks: PATCH /api/admin/academy/generated/{course_id}/blocks/reorder.",
            "Click \"Regenerate\" on a block → POST .../blocks/{block_id}/regenerate with the desired action.",
            "The block is replaced (regenerate) or new blocks are inserted (create_quiz).",
          ]}
        />
        <FeatureCard lang={lang} icon={Languages} demo="generate-blocks"
          titleFr="Traduction automatique multi-langue"
          titleEn="Automatic multi-language translation"
          whyFr="Publier déclenche automatiquement la traduction en anglais, espagnol et portugais (background jobs)."
          whyEn="Publishing automatically triggers translation into English, Spanish and Portuguese (background jobs)."
          stepsFr={[
            "Cliquez « Publier » : POST /api/admin/academy/generated/{course_id}/publish.",
            "Un statut `pending` s'affiche pour chaque langue.",
            "Pollez /api/admin/academy/generated/{course_id}/translation-status pour suivre.",
            "Re-traduction manuelle possible via /retranslate?lang=en.",
          ]}
          stepsEn={[
            "Click \"Publish\": POST /api/admin/academy/generated/{course_id}/publish.",
            "A `pending` status appears for each language.",
            "Poll /api/admin/academy/generated/{course_id}/translation-status to track it.",
            "Manual re-translation via /retranslate?lang=en.",
          ]}
        />
        <FeatureCard lang={lang} icon={Sparkles} demo="story"
          titleFr="Générer un cours depuis une source riche"
          titleEn="Generate a course from a rich source"
          whyFr="Combinez appellations liées + docs RAG uploadés + URLs scrapées : geoVINUM AI produit un parcours entier à partir de toutes ces sources."
          whyEn="Combine linked appellations + uploaded RAG docs + scraped URLs: geoVINUM AI produces a full journey from all these sources."
          stepsFr={[
            "Onglet « Générer riche ».",
            "Renseignez titre, catégorie, thème, slugs liés, URLs sources, doc_ids RAG.",
            "POST /api/admin/academy/generate-rich (jusqu'à 170s).",
            "Draft créé avec tous les blocs, prêt à éditer.",
          ]}
          stepsEn={[
            "\"Rich generate\" tab.",
            "Enter title, category, theme, linked slugs, source URLs, RAG doc_ids.",
            "POST /api/admin/academy/generate-rich (up to 170s).",
            "Draft created with all blocks, ready to edit.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "📊 Abonnements et utilisateurs" : "📊 Subscriptions and users"} testId="admin-subs">
        <FeatureCard lang={lang} icon={LayoutGrid} demo="suggestions"
          titleFr="KPIs abonnements (MRR/ARR/Churn)"
          titleEn="Subscription KPIs (MRR/ARR/Churn)"
          whyFr="Compteurs live des abonnés Amateur/Pro, revenu récurrent mensuel, taux de conversion, churn 30j — 100% source DB locale."
          whyEn="Live counts of Amateur/Pro subscribers, monthly recurring revenue, conversion rate, 30-day churn — 100% from local DB."
          stepsFr={[
            "GET /api/admin/subscriptions/stats.",
            "Retourne : counts (amateur_active, pro_active, free), MRR/ARR, growth (new_paid_week/month, cancelled_month, conversion %, churn %).",
            "Les comptes admin sont exclus des compteurs pour ne pas polluer les métriques.",
          ]}
          stepsEn={[
            "GET /api/admin/subscriptions/stats.",
            "Returns: counts (amateur_active, pro_active, free), MRR/ARR, growth (new_paid_week/month, cancelled_month, conversion %, churn %).",
            "Admin accounts are excluded from the counters to keep metrics clean.",
          ]}
        />
        <FeatureCard lang={lang} icon={ListChecks} demo="bulk-toggle"
          titleFr="Liste des abonnés"
          titleEn="Subscribers list"
          whyFr="Liste triée des abonnés payants avec email, plan, statut, période, date de démarrage."
          whyEn="Sorted list of paying subscribers with email, plan, status, period, start date."
          stepsFr={[
            "GET /api/admin/subscriptions/list?plan=amateur&status=active&limit=100.",
            "Filtrez par plan ou statut pour ciblage marketing.",
          ]}
          stepsEn={[
            "GET /api/admin/subscriptions/list?plan=amateur&status=active&limit=100.",
            "Filter by plan or status for marketing targeting.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "📚 RAG — Documents pédagogiques" : "📚 RAG — Pedagogical documents"} testId="admin-rag">
        <FeatureCard lang={lang} icon={FileText} demo="story"
          titleFr="Indexer des documents pour enrichir geoVINUM AI"
          titleEn="Index documents to enrich geoVINUM AI"
          whyFr="Uploadez livres, articles, PDF, DOCX. Les passages pertinents sont injectés automatiquement dans le contexte des réponses IA."
          whyEn="Upload books, articles, PDF, DOCX. Relevant passages are auto-injected into the AI answers' context."
          stepsFr={[
            "Back-office → « RAG · Documents internes ».",
            "Titre + source + contenu (ou upload de fichier).",
            "Le document est découpé en chunks, embeddings calculés, stocké.",
            "Toutes les conversations et générations utilisent ces docs comme sources.",
          ]}
          stepsEn={[
            "Back-office → \"RAG · Internal documents\".",
            "Title + source + content (or file upload).",
            "The document is chunked, embeddings computed, stored.",
            "All conversations and generations use these docs as sources.",
          ]}
        />
      </Section>

      <Section title={lang === "fr" ? "🔧 Endpoints API principaux" : "🔧 Main API endpoints"} testId="admin-endpoints">
        <div className="rounded-2xl border border-[#1F3A5F]/12 bg-[#FBF7F3] p-5 md:p-6">
          <div className="flex items-center gap-2 text-[#E5276A] mb-3">
            <Terminal className="w-4 h-4" />
            <span className="font-bold text-sm uppercase tracking-wider">
              {lang === "fr" ? "Référence rapide" : "Quick reference"}
            </span>
          </div>
          <div className="space-y-3 text-xs font-mono text-[#1F3A5F]">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8B95A1] mb-1">
                {lang === "fr" ? "Atlas Blocks (auth admin requise)" : "Atlas Blocks (admin auth required)"}
              </div>
              <ul className="space-y-1 text-[#4A5568]">
                <li>GET  /api/admin/atlas-blocks/{`{entity_type}/{slug}`}</li>
                <li>POST /api/admin/atlas-blocks/generate</li>
                <li>GET  /api/admin/atlas-blocks/jobs/{`{job_id}`}</li>
                <li>PATCH /api/admin/atlas-blocks/{`{block_id}`}</li>
                <li>PATCH /api/admin/atlas-blocks/bulk</li>
                <li>POST /api/admin/atlas-blocks/reorder</li>
                <li>DELETE /api/admin/atlas-blocks/{`{block_id}`}</li>
                <li>POST /api/admin/atlas-blocks/{`{block_id}`}/media</li>
                <li>GET  /api/admin/atlas-blocks/duplicates</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8B95A1] mb-1">
                {lang === "fr" ? "Atlas Blocks (public, sans auth)" : "Atlas Blocks (public, no auth)"}
              </div>
              <ul className="space-y-1 text-[#4A5568]">
                <li>GET  /api/public/atlas-blocks/{`{entity_type}/{slug}`}</li>
                <li>GET  /api/atlas-blocks/{`{entity_type}/{slug}`} (auth optionnelle)</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8B95A1] mb-1">Academy Composer</div>
              <ul className="space-y-1 text-[#4A5568]">
                <li>POST /api/admin/academy/generate</li>
                <li>POST /api/admin/academy/generate-from-url</li>
                <li>POST /api/admin/academy/generate-from-upload</li>
                <li>POST /api/admin/academy/generate-rich</li>
                <li>POST /api/admin/academy/compose</li>
                <li>GET  /api/admin/academy/generated</li>
                <li>PUT  /api/admin/academy/generated/{`{course_id}`}</li>
                <li>POST /api/admin/academy/generated/{`{course_id}`}/publish</li>
                <li>POST /api/admin/academy/generated/{`{course_id}`}/retranslate?lang=en</li>
                <li>PATCH /api/admin/academy/generated/{`{course_id}`}/blocks/reorder</li>
                <li>POST /api/admin/academy/generated/{`{course_id}`}/blocks/{`{block_id}`}/regenerate</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8B95A1] mb-1">
                {lang === "fr" ? "Abonnements et users" : "Subscriptions and users"}
              </div>
              <ul className="space-y-1 text-[#4A5568]">
                <li>GET  /api/admin/subscriptions/stats</li>
                <li>GET  /api/admin/subscriptions/list</li>
              </ul>
            </div>
          </div>
          <p className="text-xs text-[#8B95A1] mt-4">
            {lang === "fr"
              ? "Toutes les routes /admin/* requièrent un header Authorization Bearer avec un token JWT d'un utilisateur ayant role=\"admin\"."
              : "Every /admin/* route requires an Authorization Bearer header with a JWT of a user with role=\"admin\"."}
          </p>
        </div>
      </Section>
    </>
  );
}

/* ─── Main page component ─────────────────────────────────────────── */
export default function Guide() {
  const { lang } = useT();
  const [role, setRole] = useState("visitor");
  const [demo, setDemo] = useState(null); // { variant, title }
  const [downloading, setDownloading] = useState(false);
  const contentRef = useRef(null);

  const activeRole = useMemo(() => ROLES.find((r) => r.key === role), [role]);

  // ─── PDF export ──────────────────────────────────────────────
  const downloadPdf = async () => {
    if (!contentRef.current || downloading) return;
    setDownloading(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const filename = `geovinum-${role}-guide-${lang}.pdf`;
      await html2pdf()
        .set({
          margin:       [10, 8, 12, 8],
          filename,
          image:        { type: "jpeg", quality: 0.92 },
          html2canvas:  { scale: 2, useCORS: true, logging: false },
          jsPDF:        { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak:    { mode: ["avoid-all", "css", "legacy"] },
        })
        .from(contentRef.current)
        .save();
    } catch (e) {
      // Fallback to browser print dialog if html2pdf fails.
      window.print();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <DemoCtx.Provider value={(cfg) => setDemo(cfg)}>
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="guide-page">
      {/* Header */}
      <span className="chip-pink inline-flex items-center gap-1.5">
        <BookOpen className="w-3 h-3" /> {lang === "fr" ? "Mode d'emploi" : "User guide"}
      </span>
      <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1F3A5F] mt-4 leading-[1.05]">
        {lang === "fr" ? "Toutes les fonctionnalités, par rôle" : "Every feature, by role"}
      </h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">
        {lang === "fr"
          ? "Sélectionnez votre profil pour découvrir précisément ce que vous pouvez faire dans geoVINUM, avec un parcours étape par étape pour chaque fonctionnalité."
          : "Pick your profile to see exactly what you can do in geoVINUM, with a step-by-step walkthrough for every feature."}
      </p>

      {/* ─── What's new (Feb 2026) ──────────────────────────────── */}
      <div
        data-testid="guide-whats-new"
        className="mt-6 rounded-2xl bg-gradient-to-br from-[#E5276A] via-[#C01661] to-[#1F3A5F] text-white p-5 md:p-7 shadow-lg"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold opacity-90">
          <Sparkles className="w-3.5 h-3.5" />
          {lang === "fr" ? "Nouveautés · Février 2026" : "What's new · February 2026"}
        </div>
        <h2 className="font-display text-2xl md:text-3xl mt-2 leading-tight">
          {lang === "fr"
            ? "La V2 conversationnelle de geoVINUM IA est en ligne"
            : "The conversational V2 of geoVINUM AI is live"}
        </h2>
        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
          <li className="flex gap-2 items-start" data-testid="guide-new-photo-tasting">
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <span>
              <b>{lang === "fr" ? "Dégustation par photo" : "Photo tasting"}</b>
              {" — "}
              {lang === "fr"
                ? "Carnet › Photo (ou l'appareil photo de geoVINUM IA) : l'IA lit l'étiquette, trouve la photo de la bouteille et suggère robe / nez / bouche."
                : "Notebook › Photo (or the camera in geoVINUM AI): the AI reads the label, finds the bottle photo and suggests colour / nose / palate."}
            </span>
          </li>
          <li className="flex gap-2 items-start" data-testid="guide-new-chat-tasting">
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <span>
              <b>{lang === "fr" ? "Noter une dégustation dans le chat" : "Log a tasting in the chat"}</b>
              {" — "}
              {lang === "fr"
                ? "Décrivez un vin à geoVINUM IA : la fiche s'affiche, se corrige et s'enregistre dans votre carnet."
                : "Describe a wine to geoVINUM AI: the sheet appears, can be edited and saved to your notebook."}
            </span>
          </li>

          <li className="flex gap-2 items-start">
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <span>
              <b>{lang === "fr" ? "4 piliers conversationnels" : "4 conversational pillars"}</b>
              {" — "}
              {lang === "fr"
                ? "Experience, Learn, Taste, Play — un hub IA par pilier, avec saisie libre."
                : "Experience, Learn, Taste, Play — one AI hub per pillar, with free-text input."}
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <span>
              <b>{lang === "fr" ? "IA multilingue stricte" : "Strictly multilingual AI"}</b>
              {" — "}
              {lang === "fr"
                ? "Toute génération IA respecte la langue de l'app (FR / EN / ES / PT)."
                : "Every AI output respects the active app locale (FR / EN / ES / PT)."}
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <span>
              <b>{lang === "fr" ? "Voyages Academy dynamiques" : "Dynamic Academy journeys"}</b>
              {" — "}
              {lang === "fr"
                ? "Narration DeepSeek par étape + Mastery Quiz final auto-généré."
                : "Per-step DeepSeek narration + auto-generated final Mastery Quiz."}
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <span>
              <b>{lang === "fr" ? "Devine l'appellation déplacé" : "Guess the appellation moved"}</b>
              {" — "}
              {lang === "fr"
                ? "Le mini-jeu Audrey rejoint le pilier Play (anciennement Taste)."
                : "The Audrey mini-game moved from Taste to the Play pillar."}
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <span>
              <b>{lang === "fr" ? "Désambiguation des régions" : "Region disambiguation"}</b>
              {" — "}
              {lang === "fr"
                ? "Cliquer « Bordeaux » propose Saint-Émilion, Pomerol, Margaux… au lieu d'en choisir une au hasard."
                : "Clicking \"Bordeaux\" suggests Saint-Émilion, Pomerol, Margaux… instead of picking one randomly."}
            </span>
          </li>
          <li className="flex gap-2 items-start">
            <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
            <span>
              <b>{lang === "fr" ? "Séquences email nurturing" : "Nurturing email sequences"}</b>
              {" — "}
              {lang === "fr"
                ? "Suivi email post-inscription et post-opt-in, tracking pixel + clic branché au CRM."
                : "Post-signup and post-opt-in email follow-ups, with pixel + click tracking plugged into the CRM."}
            </span>
          </li>
        </ul>
      </div>

      {/* Role switcher */}
      <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="guide-role-switcher">
        {ROLES.map((r) => {
          const RIcon = r.icon;
          const isActive = role === r.key;
          const meta = lang === "fr" ? r.fr : r.en;
          return (
            <button
              key={r.key}
              data-testid={`guide-role-${r.key}`}
              onClick={() => setRole(r.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-colors ${
                isActive
                  ? "bg-[#E5276A] border-[#E5276A] text-white"
                  : "bg-white border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]/40 hover:bg-[#FFF8FB]"
              }`}
            >
              <RIcon className="w-4 h-4" />
              <div className="text-left leading-tight">
                <div className="font-display text-sm">{meta.name}</div>
                <div className={`text-[10px] uppercase tracking-widest font-bold ${isActive ? "text-white/80" : "text-[#8B95A1]"}`}>
                  {meta.tag}
                </div>
              </div>
            </button>
          );
        })}
        <button
          onClick={downloadPdf}
          disabled={downloading}
          data-testid="guide-download-pdf"
          className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#1F3A5F] bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870] disabled:opacity-60 disabled:cursor-wait transition-colors"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {downloading
            ? (lang === "fr" ? "Génération…" : "Generating…")
            : (lang === "fr" ? "Télécharger en PDF" : "Download as PDF")}
        </button>
        <a
          href="/geovinum-guide-complet-fr.pdf"
          download
          data-testid="guide-download-complet-pdf"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E5276A] text-[#E5276A] text-sm font-bold hover:bg-[#FFF8FB] transition-colors"
        >
          <Download className="w-4 h-4" />
          {lang === "fr" ? "Guide complet (5 rôles)" : "Full guide (5 roles)"}
        </a>
      </div>

      {/* Content (also the PDF export target) */}
      <div ref={contentRef} className="mt-8" data-testid={`guide-content-${role}`}>
        {role === "visitor" && <VisitorContent lang={lang} />}
        {role === "free" && <FreeContent lang={lang} />}
        {role === "amateur" && <AmateurContent lang={lang} />}
        {role === "pro" && <ProContent lang={lang} />}
        {role === "admin" && <AdminContent lang={lang} />}
      </div>

      {/* Credits explainer + CTA */}
      {role !== "admin" && (
        <div className="mt-12 rounded-2xl bg-[#FBF7F3] border border-[#1F3A5F]/10 p-6 md:p-8" data-testid="guide-credits">
          <div className="flex items-center gap-2 text-[#E5276A]">
            <Coins className="w-5 h-5" />
            <h3 className="font-display text-2xl text-[#1F3A5F]">
              {lang === "fr" ? "Comment fonctionnent les crédits ?" : "How do credits work?"}
            </h3>
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm text-[#4A5568]">
            <ul className="space-y-2">
              <li><b className="text-[#1F3A5F]">Free</b> : {lang === "fr" ? "0 crédit · quotas quotidiens en localStorage" : "0 credits · daily quotas in localStorage"}</li>
              <li><b className="text-[#1F3A5F]">Amateur</b> : {lang === "fr" ? "100 crédits/mois (renouvelés au 1er du mois)" : "100 credits/month (renewed on the 1st)"}</li>
              <li><b className="text-[#1F3A5F]">Pro</b> : {lang === "fr" ? "300 crédits/mois" : "300 credits/month"}</li>
            </ul>
            <ul className="space-y-2">
              <li><b className="text-[#E5276A]">{lang === "fr" ? "Carnet IA" : "AI Notebook"}</b> : {lang === "fr" ? "4 crédits/structuration" : "4 credits/entry"}</li>
              <li><b className="text-[#E5276A]">{lang === "fr" ? "Itinéraire IA" : "AI Itinerary"}</b> : {lang === "fr" ? "8 crédits" : "8 credits"}</li>
              <li><b className="text-[#E5276A]">Story Builder</b> : {lang === "fr" ? "6 crédits (pack complet)" : "6 credits (full pack)"}</li>
              <li><b className="text-[#E5276A]">Map Builder</b> : {lang === "fr" ? "10 crédits/artefact" : "10 credits/artefact"}</li>
              <li><b className="text-[#E5276A]">{lang === "fr" ? "Jeu sponsorisé (Pro)" : "Sponsored game (Pro)"}</b> : {lang === "fr" ? "20 crédits" : "20 credits"}</li>
            </ul>
          </div>
        </div>
      )}

      {/* Final CTA */}
      <div className="mt-10 flex flex-wrap gap-3 justify-center">
        {role === "visitor" && (
          <Link to="/register" data-testid="guide-cta-register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
            <UserPlus className="w-4 h-4" /> {lang === "fr" ? "Créer mon compte gratuit" : "Create my free account"}
          </Link>
        )}
        {(role === "free" || role === "amateur") && (
          <Link to="/pricing" data-testid="guide-cta-pricing"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
            <Crown className="w-4 h-4" /> {lang === "fr" ? "Comparer les abonnements" : "Compare plans"}
          </Link>
        )}
        {role === "pro" && (
          <Link to="/pro/dashboard" data-testid="guide-cta-pro"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
            <Building2 className="w-4 h-4" /> {lang === "fr" ? "Aller au Dashboard Pro" : "Open Pro Dashboard"}
          </Link>
        )}
        {role === "admin" && (
          <a href="https://geovinum.io/admin" target="_blank" rel="noopener noreferrer" data-testid="guide-cta-admin"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870]">
            <ShieldCheck className="w-4 h-4" /> {lang === "fr" ? "Ouvrir le back-office" : "Open back-office"}
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
    <DemoModal
      open={!!demo}
      onClose={() => setDemo(null)}
      variant={demo?.variant}
      title={demo?.title}
      lang={lang}
    />
    </DemoCtx.Provider>
  );
}
